import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';

import { updateApplicationStatus } from '../../action/application/update-application-status';
import { loadHrisIntegrationResponse } from '../../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../../hris-integration-response/hris-integration-response-selectors';
import { createMergeEmployee } from '../../merge-integration/actions/create-merge-employee';
import { loadMergeIntegration } from '../../merge-integration/actions/load-merge-integration';
import { mergeIntegrationForOrgIdSelector } from '../../merge-integration/merge-integration-selectors';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeeSelector } from '../../reducers/employee-selectors';
import { deleteEmployee } from '../actions/delete-employee';
import { loadEmployee } from '../actions/load-employee';

import { EmployeeListIntegrationDisabler } from '../../employee-list-integration/employee-list-integration-disabler';
import { EmployeeMergeWizard } from '../../employee-merger/employee-merge-wizard';
import { UiButton } from '../../ui/ui-button';
import { UiKebabMenu } from '../../ui/ui-kebab-menu';
import { employeeMessagesRoute } from '../../web-routes';
import { EmployeeStatus } from '../employee-status';
import { EmployeeTeamEngineBadge } from '../employee-team-engine-badge';
import { EmployeeUserDialog } from '../employee-user-dialog';
import { EndEmploymentDialog } from '../end-employment-dialog';
import { EmployeeEndDateDialog } from '../loadable-components';

import { EmployeeProfileView } from './employee-profile-view';
import { EmployeeRehireDialog } from './employee-rehire-dialog';

import { EMPLOYED_STATUS, VALIDATING_STATUS } from '../../config/application-status-config';
import { getHrisDisplayName } from '../../merge-integration/util/merge-display-utils';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { confirm } from '../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../widgets/loading-message';
import { NotFoundPage } from '../../widgets/not-found-page';
import { employeeName } from '../utils/employee-name';

interface InputProps {
  employeeId: number;
  closeDialog?: () => void;
  tab?: string | null;
}

const mapStateToProps = (state: any, { employeeId }: InputProps) => {
  const employee = employeeSelector(state, employeeId);

  return {
    employee,
    mergeIntegration: employee ? mergeIntegrationForOrgIdSelector(state, employee.orgId) : null,
    hrisIntegrationResponse: employee
      ? hrisIntegrationResponseForOrgIdSelector(state, employee.orgId)
      : null,
    currentUser: userSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employeeId }: InputProps,
) => ({
  actions: {
    loadEmployee: (notFoundFunction: () => void) =>
      dispatch(loadEmployee(employeeId, notFoundFunction)),
    deleteEmployee: (employee: Employee, options: { disableNotification?: boolean } = {}) =>
      dispatch(deleteEmployee(employee, options)),
    createMergeEmployee: (orgId: number, integrationName?: string | null) =>
      dispatch(createMergeEmployee(orgId, employeeId, integrationName)),
    loadMergeIntegration: (orgId: number) => dispatch(loadMergeIntegration(orgId)),
    loadHrisIntegrationResponse: (orgId: number) => dispatch(loadHrisIntegrationResponse(orgId)),
    unhireEmployee: (
      jobApplicantId: number,
      jobId: number,
      orgId: number,
      hasEmployeesPermission: boolean,
    ) =>
      dispatch(
        updateApplicationStatus(
          jobApplicantId,
          jobId,
          orgId,
          EMPLOYED_STATUS,
          {
            status: VALIDATING_STATUS,
          },
          hasEmployeesPermission,
        ),
      ),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeProfile = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    employeeId,
    employee,
    currentUser,
    mergeIntegration,
    hrisIntegrationResponse,
    actions,
    closeDialog,
    tab,
  }: Props) => {
    const [isShowingEndEmploymentDialog, setIsShowingEndEmploymentDialog] =
      useState<boolean>(false);
    const [isShowingEndDateDialog, setIsShowingEndDateDialog] = useState<boolean>(false);
    const [isShowingEmployeeUserDialog, setIsShowingEmployeeUserDialog] = useState<boolean>(false);
    const [isShowingRehireDialog, setIsShowingRehireDialog] = useState<boolean>(false);
    const [isShowingMergeDialog, setIsShowingMergeDialog] = useState<boolean>(false);
    const [employeeNotFound, setEmployeeNotFound] = useState(false);

    const hasEmployeesPermission = currentUser.employeesPermission !== EMPLOYEE_PERMISSION.NONE;

    const history = useHistory();

    useEffect(() => {
      actions.loadEmployee(handleNotFound);
      if (employee?.orgId && currentUser.isAccountAdmin) {
        actions.loadMergeIntegration(employee.orgId);
        actions.loadHrisIntegrationResponse(employee.orgId);
      }
      // It is not necessary to add the dependency on currentUser here
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, actions, employee]);

    const toggleEmployeeUserDialog = () => {
      setIsShowingEmployeeUserDialog(!isShowingEmployeeUserDialog);
    };

    const openEndEmploymentDialog = () => {
      setIsShowingEndEmploymentDialog(true);
    };

    const closeEndEmploymentDialog = () => {
      setIsShowingEndEmploymentDialog(false);
    };

    const saveEndEmploymentCallback = () => {
      closeEndEmploymentDialog();
      setIsShowingEndDateDialog(true);
    };

    const closeEndDateDialog = () => {
      setIsShowingEndDateDialog(false);
    };

    const handleMessageClick = () => {
      history.push(
        employeeMessagesRoute({ orgId: employee.orgId, employeeId: employee.employeeId }),
      );
    };

    const handleRehireDialogToggle = () => {
      setIsShowingRehireDialog(!isShowingRehireDialog);
    };

    const openMergeDialog = () => {
      setIsShowingMergeDialog(true);
    };

    const closeMergeDialog = () => {
      setIsShowingMergeDialog(false);
    };

    const confirmationMessage = (actionToConfirm: 'delete' | 'move_to_hiring') => {
      const name = employeeName(employee);
      return (
        <>
          Are you sure you want to{' '}
          {actionToConfirm === 'delete'
            ? `remove ${name} from your directory?`
            : `move ${name} back to hiring? This will remove them from your employee directory.`}{' '}
          You will no longer be able to message them or access their profile.
          {employee.username && (
            <span className="typo-bold">
              {' '}
              {name}&apos;s user will be disabled and they will lose access to the software until
              re-enabled.
            </span>
          )}
        </>
      );
    };

    const handleDeleteClick = async () => {
      const confirmation = await confirm(confirmationMessage('delete'));
      if (confirmation) {
        actions.deleteEmployee(employee);
        handleNotFound();
        if (closeDialog) {
          closeDialog();
        }
      }
    };

    const handleNotFound = () => {
      setEmployeeNotFound(true);
    };

    const handleCreateMergeEmployeeClick = () => {
      actions.createMergeEmployee(
        employee.orgId,
        mergeIntegration?.HrisIntegrationType?.integrationName,
      );
    };

    const handleMoveBackClick = async () => {
      const { jobApplicantId, jobId, orgId } = employee;
      if (!(jobApplicantId && jobId)) {
        // we shouldn't be here - this menu option is not available if there is not a jobApplicantId + jobId on the employee
        return;
      }

      const confirmation = await confirm(confirmationMessage('move_to_hiring'));

      if (confirmation) {
        actions.unhireEmployee(jobApplicantId, jobId, orgId, hasEmployeesPermission);
        actions.deleteEmployee(employee, { disableNotification: true });
        if (closeDialog) {
          closeDialog();
        }
      }
    };

    if (employeeNotFound) {
      return <NotFoundPage message={"We're sorry. We could not find this employee."} />;
    }

    if (!employee) {
      return <LoadingMessage />;
    }

    const canGrantAccess =
      (currentUser.isAccountAdmin || currentUser.isAdmin) && !employee.username;
    const isEmployeeThisUser = employee.username === currentUser.username;
    const hrisDisplayName = getHrisDisplayName(mergeIntegration, hrisIntegrationResponse);

    return (
      <div>
        <div>
          <div className="u-spread u-margin-bottom">
            <div>
              <div className="typo-h5 u-display-flex u-alignItems-center">
                <div>{employeeName(employee)}</div>
                {employee.username && (
                  <Tooltip title="Team Engine User" placement="top">
                    <span>
                      <MaterialDesignIcon
                        iconName={ICON_NAMES.VERIFIED_USER}
                        color="primary"
                        className="u-margin-left-sm u-margin-top"
                        size="lg"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
              <EmployeeTeamEngineBadge employeeId={employee.employeeId} />
              {!employee.isCurrentEmployee ? <EmployeeStatus employee={employee} /> : null}
            </div>
            <div className="u-textAlign-right u-display-flex">
              {canGrantAccess && (
                <UiButton
                  className="u-margin-sm u-alignSelf-center"
                  variant="tertiary"
                  onClick={toggleEmployeeUserDialog}
                >
                  Grant Access
                </UiButton>
              )}
              <UiButton
                className="u-margin-sm u-alignSelf-center"
                variant="tertiary"
                onClick={handleMessageClick}
              >
                Message
              </UiButton>
              {employee.isCurrentEmployee && !isEmployeeThisUser && (
                <UiButton
                  className="u-margin-sm u-alignSelf-center"
                  variant="tertiary"
                  onClick={openEndEmploymentDialog}
                >
                  Terminate
                </UiButton>
              )}
              {!employee.isCurrentEmployee && (
                <UiButton
                  className="u-margin-sm u-alignSelf-center"
                  variant="tertiary"
                  onClick={handleRehireDialogToggle}
                >
                  Rehire
                </UiButton>
              )}
              {/* TODO PROD-1977 writes need to be tested with each integration until programmatic writes are implemented */}
              {!employee.integrationId &&
                mergeIntegration?.HrisIntegrationType?.integrationName?.toLowerCase() ===
                  'bamboohr' && (
                  <UiButton
                    className="u-margin-sm u-alignSelf-center"
                    variant="tertiary"
                    onClick={handleCreateMergeEmployeeClick}
                  >
                    Create in {hrisDisplayName || 'HRIS'}
                  </UiButton>
                )}
              <EmployeeListIntegrationDisabler
                employee={employee}
                integrationName={hrisDisplayName}
              >
                <UiKebabMenu menuId={`merge-employee-${employee.employeeId}`}>
                  <MenuItem onClick={openMergeDialog}>Combine Employees</MenuItem>
                  <Tooltip
                    title={
                      isEmployeeThisUser
                        ? 'You cannot delete the employee associated with your user'
                        : ''
                    }
                  >
                    <div>
                      <MenuItem onClick={handleDeleteClick} disabled={isEmployeeThisUser}>
                        Delete
                      </MenuItem>
                    </div>
                  </Tooltip>
                  {employee.jobApplicantId && employee.jobId && (
                    <MenuItem onClick={handleMoveBackClick}>Move Back to Hiring</MenuItem>
                  )}
                </UiKebabMenu>
              </EmployeeListIntegrationDisabler>
            </div>
          </div>
          <EmployeeProfileView employee={employee} tab={tab} />
        </div>
        {isShowingEndEmploymentDialog && (
          <EndEmploymentDialog
            open
            saveCallback={saveEndEmploymentCallback}
            cancelCallback={closeEndEmploymentDialog}
            employee={employee}
          />
        )}
        {isShowingEndDateDialog && (
          <EmployeeEndDateDialog
            open
            saveCallback={closeEndDateDialog}
            cancelCallback={closeEndDateDialog}
            employee={employee}
          />
        )}
        {isShowingEmployeeUserDialog && (
          <EmployeeUserDialog onClose={toggleEmployeeUserDialog} employeeId={employee.employeeId} />
        )}
        {isShowingRehireDialog && (
          <EmployeeRehireDialog employee={employee} onClose={handleRehireDialogToggle} />
        )}
        {isShowingMergeDialog && (
          <EmployeeMergeWizard
            employeeToKeepId={employee.employeeId}
            orgId={employee.orgId}
            close={closeMergeDialog}
          />
        )}
      </div>
    );
  },
);
